import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Search, Pencil, Trash2, Phone, MapPin, X } from "lucide-react";
import { toast } from "sonner";

const empty = { nome: "", telefono: "", indirizzo: "", note: "" };

export default function Clienti() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (s = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: s ? { search: s } : {} });
      setClients(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 500);
    return () => clearTimeout(t);
  }, [search, load]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  }, []);
  const openEdit = (c) => {
    setEditing(c);
    setForm({ nome: c.nome, telefono: c.telefono || "", indirizzo: c.indirizzo || "", note: c.note || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) {
      toast.error("Nome obbligatorio");
      return;
    }
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, form);
        toast.success("Cliente aggiornato");
      } else {
        await api.post("/clients", form);
        toast.success("Cliente creato");
      }
      setOpen(false);
      load(search);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const remove = useCallback(async (c) => {
    if (!window.confirm(`Eliminare ${c.nome}?`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Cliente eliminato");
      load(search);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  }, [search, load]);

  return (
    <div className="space-y-6" data-testid="clienti-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[11px] font-black uppercase tracking-[0.3em] text-[#E60000]">Anagrafica</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mt-1">
            Clienti
          </h1>
        </div>
        <button
          onClick={openCreate}
          data-testid="new-client-button"
          className="bg-[#E60000] text-white border-2 border-black px-6 py-3 font-bold uppercase tracking-widest hover:bg-black transition-colors inline-flex items-center gap-2 self-start"
        >
          <Plus className="w-4 h-4" strokeWidth={3} />
          Nuovo Cliente
        </button>
      </div>

      <div className="flex items-center border-2 border-black bg-white">
        <Search className="w-5 h-5 ml-3" strokeWidth={3} />
        <input
          data-testid="clienti-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per nome o telefono..."
          className="flex-1 p-3 outline-none font-medium"
        />
      </div>

      <div className="border-2 border-black bg-white">
        <div className="hidden md:grid grid-cols-12 border-b-4 border-black bg-white uppercase text-[10px] font-black tracking-widest p-3">
          <div className="col-span-4">Nome</div>
          <div className="col-span-3">Telefono</div>
          <div className="col-span-4">Indirizzo</div>
          <div className="col-span-1 text-right">Azioni</div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm font-bold uppercase">Caricamento...</div>
        ) : clients.length === 0 ? (
          <div className="p-8 text-center text-sm font-bold uppercase">Nessun cliente trovato</div>
        ) : (
          clients.map((c) => (
            <div
              key={c.id}
              data-testid={`client-row-${c.id}`}
              className="grid grid-cols-1 md:grid-cols-12 border-b-2 border-black last:border-b-0 p-3 hover:bg-black hover:text-white transition-colors group items-center gap-2"
            >
              <div className="md:col-span-4 font-bold">{c.nome}</div>
              <div className="md:col-span-3 flex items-center gap-2 font-mono-data text-sm">
                <Phone className="w-3.5 h-3.5" />{c.telefono || "—"}
              </div>
              <div className="md:col-span-4 flex items-center gap-2 text-sm">
                <MapPin className="w-3.5 h-3.5" />{c.indirizzo || "—"}
              </div>
              <div className="md:col-span-1 flex md:justify-end gap-2">
                <button
                  data-testid={`edit-client-${c.id}`}
                  onClick={() => openEdit(c)}
                  className="p-2 border-2 border-black bg-white text-black group-hover:bg-white group-hover:text-black hover:bg-[#E60000] hover:!text-white hover:!border-black"
                >
                  <Pencil className="w-4 h-4" strokeWidth={3} />
                </button>
                <button
                  data-testid={`delete-client-${c.id}`}
                  onClick={() => remove(c)}
                  className="p-2 border-2 border-black bg-[#E60000] text-white hover:bg-black"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={3} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={save}
            data-testid="client-form-modal"
            className="bg-white border-2 border-black brutal-shadow-lg max-w-lg w-full p-6 space-y-4"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-display font-black text-2xl uppercase">
                {editing ? "Modifica" : "Nuovo"} Cliente
              </h2>
              <button type="button" onClick={() => setOpen(false)} className="p-1 border-2 border-black hover:bg-black hover:text-white">
                <X className="w-4 h-4" strokeWidth={3} />
              </button>
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-widest mb-2">Nome / Ragione sociale *</label>
              <input
                data-testid="client-form-nome"
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000]"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-widest mb-2">Telefono</label>
              <input
                data-testid="client-form-telefono"
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-widest mb-2">Indirizzo</label>
              <input
                data-testid="client-form-indirizzo"
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000]"
                value={form.indirizzo}
                onChange={(e) => setForm({ ...form, indirizzo: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-black uppercase tracking-widest mb-2">Note</label>
              <textarea
                data-testid="client-form-note"
                rows={3}
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] resize-none"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="border-2 border-black px-5 py-2.5 font-bold uppercase text-xs tracking-widest hover:bg-black hover:text-white"
              >
                Annulla
              </button>
              <button
                type="submit"
                data-testid="client-form-submit"
                className="bg-[#E60000] text-white border-2 border-black px-5 py-2.5 font-bold uppercase text-xs tracking-widest hover:bg-black"
              >
                Salva
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
